import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import PageHeader from "components/PageHeader";
import StatusChip from "components/StatusChip";
import StatTile from "shell/StatTile";
import { API_BASE } from "config/api";

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

/** Drill-down view for one branch — members, income breakdown, today's attendance. */
function BranchDetail() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token || !branchId) return;

    setIsLoading(true);
    fetch(`${API_BASE}/branches/${branchId}/overview`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      })
      .then((json) => {
        setBranch(json.data || null);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [branchId]);

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading branch...</Typography>
      </Box>
    );
  }

  if (error || !branch) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error || "Branch not found"}</Typography>
      </Box>
    );
  }

  const incomeByType = Object.entries(branch.income_by_type || {});

  return (
    <Box>
      <PageHeader
        title={branch.name}
        subtitle={branch.address || "No address set"}
        actions={
          <>
            <StatusChip value={branch.status} />
            <Button
              variant="outlined"
              startIcon={<Icon>arrow_back</Icon>}
              onClick={() => navigate("/branches")}
            >
              Back to Branches
            </Button>
          </>
        }
      />

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <StatTile icon="groups" label="Members" value={branch.member_count} tone="primary" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="payments"
            label="Total Income"
            value={money(branch.income_total)}
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="how_to_reg"
            label="Attendance Today"
            value={branch.attendance_today}
            tone="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="h6" mb={2}>
              Income by Type
            </Typography>
            {incomeByType.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No successful payments recorded for this branch yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomeByType.map(([type, total]) => (
                    <TableRow key={type}>
                      <TableCell sx={{ textTransform: "capitalize" }}>{type}</TableCell>
                      <TableCell align="right">{money(total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="h6" mb={2}>
              Recently Joined Members
            </Typography>
            {(branch.recent_members || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No members assigned to this branch yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Membership Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branch.recent_members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        {m.first_name} {m.surname}
                      </TableCell>
                      <TableCell>{m.phone || "—"}</TableCell>
                      <TableCell align="right">
                        {m.membership_date ? new Date(m.membership_date).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BranchDetail;
